import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../apiHelpers';
import Tooltip from '../components/ui/Tooltip';
import { Panel } from '../components/ui/Panel';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import { FadeIn } from '../components/ui/FadeIn';
import {
  AirspaceStatus,
  AIRSPACE_STATUS_LABELS,
  AIRSPACE_STATUS_PILL_CLASSES,
} from '../utils/airspace-helpers';

interface QueueItem {
  id: string;
  airspace_status: AirspaceStatus;
}

// Display only the actionable statuses — 'complete' is dropped from the tile
// grid to give the four active states more room. Counts still include it in
// case a future variant wants to surface it.
const STATUSES: AirspaceStatus[] = ['needs_action', 'needs_review', 'needs_authorization', 'pending'];

/**
 * Companion card to the POA grid — surfaces the active airspace
 * authorization workload at a glance, links into /pilots/airspace.
 *
 * Visual recipe is intentionally identical to the dashboard's
 * AirspaceAuthModule (same heading, same stat tiles) so the two
 * surfaces feel like the same widget showing up in two places.
 */
const AirspaceStatsCard: React.FC = () => {
  const api = useApi();
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/airspace/queue?within_days=60', { silent: true })
      .then((d: QueueItem[]) => { setQueue(d); setLoading(false); })
      .catch(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const counts: Record<AirspaceStatus, number> = {
    needs_action: 0, needs_review: 0, needs_authorization: 0, pending: 0, complete: 0,
  };
  for (const q of queue) {
    if (q.airspace_status in counts) counts[q.airspace_status]++;
  }
  const totalActive = STATUSES.reduce((a, s) => a + counts[s], 0);

  return (
    <Panel
      title="Airspace Authorizations"
      titleStyle="section"
      accent="sky"
      lit
      actions={<Link to="/pilots/airspace" className="text-[10px] text-sky-400 hover:text-sky-300">open queue →</Link>}
      className="h-full overflow-hidden"
    >
      <FadeIn key={loading ? 'loading' : 'loaded'} className="flex flex-1 flex-col min-h-0">
      {loading ? (
        <SkeletonGroup label="Loading airspace authorizations" className="grid flex-1 grid-cols-2 content-center gap-2 sm:grid-cols-4">
          {STATUSES.map(s => <Skeleton key={s} className="h-[74px]" />)}
        </SkeletonGroup>
      ) : (
        <>
          <div className="text-xs text-on-surface-variant mb-3">
            {totalActive} event{totalActive === 1 ? '' : 's'} need attention in the next 60 days
          </div>
          <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-2 content-center">
            {STATUSES.map(s => (
              <Tooltip key={s} label={AIRSPACE_STATUS_LABELS[s]}>
              <Link
                to={`/pilots/airspace?status=${s}`}
                className={`flex flex-col items-center justify-center rounded-md py-3 ring-1 ring-inset ${AIRSPACE_STATUS_PILL_CLASSES[s]} hover:brightness-110 transition`}
              >
                <span className="text-2xl font-bold tabular-nums">{counts[s]}</span>
                <span className="text-[10px] uppercase tracking-wider mt-1 leading-tight text-center">
                  {AIRSPACE_STATUS_LABELS[s]}
                </span>
              </Link>
              </Tooltip>
            ))}
          </div>
        </>
      )}
      </FadeIn>
    </Panel>
  );
};

export default AirspaceStatsCard;
